import { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  getTeamAssets,
  getPendingAssetInspections,
  getStanddownInspectionFields,
  getAssetInspectionDetail,
  submitAssetInspection
} from './assetInspectionService';
import { validateInspectionForm, getFailedFields } from './validationUtils';
import { filterDataByTeam, isAssociateManager, getUsername } from '../auth';

export function useAssetInspectionForm({ mode = 'create' }) {
  const navigate = useNavigate();
  const { id } = useParams();
  const [assets, setAssets] = useState([]);
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [fieldValues, setFieldValues] = useState([]);
  const [groupedFields, setGroupedFields] = useState({});
  const [images, setImages] = useState([]);
  const [existingImages, setExistingImages] = useState([]);
  const [imagesToDelete, setImagesToDelete] = useState([]);
  const [success, setSuccess] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [formErrors, setFormErrors] = useState({});
  const [showPopup, setShowPopup] = useState(false);
  const [popupContent, setPopupContent] = useState("");
  const [popupError, setPopupError] = useState(false);
  const [imageError, setImageError] = useState(null);
  const [pendingInspection, setPendingInspection] = useState([]);
  const [loaded, setLoaded] = useState(true);
  const [assetInspection, setAssetInspection] = useState({
    reInspection: false,
    serviceNeeded: false,
    status: 'pending',
    comments: '',
    reason: ''
  });
  const [showConfirmPopup, setShowConfirmPopup] = useState(false);
  const [statusConfirmPopup, setStatusConfirmPopup] = useState(false);
  const [failedFields, setFailedFields] = useState([]);
  const [standdownEnabled, setStanddownEnabled] = useState(true);
  const standdownLoadSeq = useRef(0);
  // Ref avoids stale closure on OK after approve/deny success (state alone can lag the handler).
  const redirectAfterPopupCloseRef = useRef(false);

  const user_info = filterDataByTeam();
  const isEdit = mode === 'edit';

  useEffect(() => {
    if (!isEdit) {
      setLoaded(false);
      getTeamAssets(user_info.associatedTeamsIds)
        .then(setAssets)
        .catch(err => {
          setShowPopup(true);
          setPopupError(true);
          setPopupContent(err.message);
        })
        .finally(() => setLoaded(true));
    }
  }, []);

  useEffect(() => {
    if (isEdit && id) {
      setLoaded(false);
      getAssetInspectionDetail(id)
        .then(respJson => {
          setSelectedAsset({ id: respJson.asset, name: respJson.assetName });
          const values = respJson.fieldValues.map((fv, index) => ({
            index,
            field: fv.field,
            name: fv.fieldName,
            section: fv.section,
            value: fv.value,
          }));
          setFieldValues(values);
          setExistingImages(respJson.images || []);
          setAssetInspection({
            id: respJson.id,
            inspectedByName: respJson.inspectedByUserInfo?.name ?? '',
            approvedBy: respJson.approvedBy,
            inspectionDate: respJson.inspectionDate,
            approvedDate: respJson.approvedDate,
            reInspection: respJson.reInspection,
            serviceNeeded: respJson.serviceNeeded,
            reInspectionDate: respJson.reInspectionDate,
            status: respJson.status,
            comments: respJson.comments,
            reason: respJson.denialReason,
            reInspectedByName: respJson.reInspectedByName,
            approvedByName: respJson.approvedByName,
            isInspectedByAM: isAssociateManager() && (getUsername() === respJson.inspectedByUserInfo?.userId)
          });
        })
        .catch(err => {
          setShowPopup(true);
          setPopupError(true);
          setPopupContent(err.message);
        })
        .finally(() => setLoaded(true));
    }
  }, [id]);

  useEffect(() => {
    if (mode === 'create' && selectedAsset) {
      const requestSeq = ++standdownLoadSeq.current;
      const assetId = selectedAsset.id;
      setLoaded(false);
      Promise.all([
        getStanddownInspectionFields(assetId),
        getPendingAssetInspections(assetId)
      ])
        .then(([payload, pending]) => {
          if (requestSeq !== standdownLoadSeq.current) {
            return;
          }
          setPendingInspection(pending);
          if (!(payload.length > 0)) {
            setStanddownEnabled(false);
            setFieldValues([]);
            return;
          }
          setStanddownEnabled(true);
          const values = (payload || []).map((field, index) => ({
            index,
            field: field.id,
            name: field.label,
            section: field.section,
            value: '',
          }));
          setFieldValues(values);
        })
        .catch(err => {
          if (requestSeq !== standdownLoadSeq.current) {
            return;
          }
          setShowPopup(true);
          setPopupError(true);
          setPopupContent(err.message);
        })
        .finally(() => {
          if (requestSeq === standdownLoadSeq.current) {
            setLoaded(true);
          }
        });
    }
  }, [selectedAsset]);

  useEffect(() => {
    const grouped = fieldValues.reduce((acc, field) => {
      const section = field.section || 'General';
      if (!acc[section]) acc[section] = [];
      acc[section].push(field);
      return acc;
    }, {});
    setGroupedFields(grouped);
  }, [fieldValues]);

  const handleAssetChange = (e, { value }) => {
    const asset = assets.find(a => a.id === value);
    setSelectedAsset(asset);
    setStanddownEnabled(true);
    setFieldValues([]);
    setImageError(null);
  };

  const handleChange = (index, value) => {
    const updated = [...fieldValues];
    updated[index].value = value;
    setFieldValues(updated);
  };

  const handleFieldChange = (key, value) => {
    const inspectionDetailCopy = structuredClone(assetInspection);
    inspectionDetailCopy[key] = value;
    setAssetInspection(inspectionDetailCopy);
  };

  const handleImageChange = (e) => {
    setImages([...images, ...Array.from(e.target.files)]);
    setImageError(null);
  };

  const handleRemoveImage = (index) => {
    const updated = [...images];
    updated.splice(index, 1);
    setImages(updated);
    setImageError(null);
  };

  const handleImageDelete = (imageId) => {
    setImagesToDelete(prev => [...prev, imageId]);
    setExistingImages(prev => prev.filter(img => img.id !== imageId));
  };

  const prepareFormData = (status, isDraft = false) => {
    const formData = new FormData();
    formData.append('asset', selectedAsset.id);

    const fieldValuesList = fieldValues.map(f => ({
      field: f.field,
      value: f.value,
      section: f.section
    }));

    formData.append('field_values', JSON.stringify(fieldValuesList));
    formData.append('inspection_date', new Date().toISOString().substring(0, 10));

    if (mode === 'edit' && !isDraft) {
      formData.append('re_inspection_date', new Date().toISOString().substring(0, 10));
    }

    formData.append('service_needed', assetInspection.serviceNeeded);
    formData.append('re_inspection', assetInspection.reInspection);
    formData.append('comments', assetInspection.comments);

    if (!isDraft) {
      formData.append('status', status === 'submit_confirmed' ? 'pending' : status);

      if (status === "denied") {
        formData.append('denial_reason', assetInspection.reason);
      }

      if (status === "approved") {
        formData.append('approved_date', new Date().toISOString().substring(0, 10));
      }
    }

    if (imagesToDelete.length > 0) {
      formData.append("images_to_delete", JSON.stringify(imagesToDelete));
    }

    images.forEach(image => {
      formData.append('images', image);
    });

    return formData;
  };

  const getSubmissionParams = (isDraft = false) => {
    const inspectionId = mode === 'edit' ? id : null;
    return { isDraft, inspectionId };
  };

  const handleSuccessfulSubmission = async (resp, isDraft = false, submittedStatus = null) => {
    let newId = id;
    if (!id) {
      const respJson = await resp.json();
      newId = respJson['id'];
      window.history.replaceState(null, "", "/standdown/" + newId);
    }

    if (submittedStatus) {
      setAssetInspection(prev => ({
        ...prev,
        status: submittedStatus
      }));
    }

    // Show success + set redirect before refetch so approve/deny OK always navigates.
    setSuccess(true);
    setPopupContent(isDraft ? "Draft saved successfully" : "Update successful");
    setPopupError(false);
    setShowConfirmPopup(false);
    redirectAfterPopupCloseRef.current = !isDraft;
    setShowPopup(true);

    if (newId && submittedStatus) {
      try {
        const freshData = await getAssetInspectionDetail(newId);
        setAssetInspection(freshData);
      } catch (err) {
        console.error('Failed to refetch inspection data:', err);
      }
    }
  };

  const handlePopupClose = () => {
    setShowPopup(false);
    setShowConfirmPopup(false);
    if (redirectAfterPopupCloseRef.current) {
      redirectAfterPopupCloseRef.current = false;
      navigate('/standdown');
    }
  };

  const handleSubmissionError = (err) => {
    redirectAfterPopupCloseRef.current = false;
    setShowConfirmPopup(false);
    setShowPopup(true);
    setPopupContent(err.message);
    setPopupError(true);
  };

  const handleSubmit = async (status, skip_confirmation) => {
    setSuccess(false);
    redirectAfterPopupCloseRef.current = false;
    setLoaded(false);

    if (!(typeof status === "string")) {
      status = "pending";
    }

    const validation = validateInspectionForm(fieldValues, images, existingImages, assetInspection, status);
    setFieldErrors(validation.fieldErrors);
    setImageError(validation.imageError);
    setFormErrors(validation.formErrors);

    if (!validation.isValid) {
      setLoaded(true);
      return;
    }

    const failed = getFailedFields(fieldValues);
    if (failed.length > 0 && !skip_confirmation) {
      redirectAfterPopupCloseRef.current = false;
      setStatusConfirmPopup(status);
      setFailedFields(failed);
      setPopupContent("Some fields are marked as Failed. Do you want to submit anyway?");
      setShowPopup(true);
      setShowConfirmPopup(true);
      setLoaded(true);
      return;
    }

    setShowConfirmPopup(false);

    const { isDraft, inspectionId } = getSubmissionParams(false);
    const formData = prepareFormData(status, false);

    try {
      const resp = await submitAssetInspection(formData, isDraft, inspectionId);
      await handleSuccessfulSubmission(resp, false, status);
    } catch (err) {
      handleSubmissionError(err);
    } finally {
      setLoaded(true);
    }
  };

  const handleSaveDraft = async () => {
    setSuccess(false);
    redirectAfterPopupCloseRef.current = false;
    setLoaded(false);

    const validation = validateInspectionForm(fieldValues, images, existingImages, assetInspection, 'draft');
    setFieldErrors(validation.fieldErrors);

    if (!validation.isValid) {
      setLoaded(true);
      return;
    }

    const { isDraft, inspectionId } = getSubmissionParams(true);
    const formData = prepareFormData(null, true);

    try {
      const resp = await submitAssetInspection(formData, isDraft, inspectionId);
      await handleSuccessfulSubmission(resp, true);
    } catch (err) {
      handleSubmissionError(err);
    } finally {
      setLoaded(true);
    }
  };

  const handleApprove = () => {
    handleSubmit("approved", false);
  };

  const handleDeny = () => {
    handleSubmit("denied", false);
  };

  return {
    assets,
    selectedAsset,
    fieldValues,
    groupedFields,
    images,
    existingImages,
    imagesToDelete,
    success,
    fieldErrors,
    formErrors,
    showPopup,
    setShowPopup,
    popupContent,
    popupError,
    imageError,
    pendingInspection,
    loaded,
    assetInspection,
    showConfirmPopup,
    setShowConfirmPopup,
    statusConfirmPopup,
    failedFields,
    standdownEnabled,
    handleAssetChange,
    handleChange,
    handleFieldChange,
    handleImageChange,
    handleRemoveImage,
    handleImageDelete,
    handleSubmit,
    handleSaveDraft,
    handleApprove,
    handleDeny,
    handlePopupClose
  };
}
